import { Card, Stack, Typography, useTheme } from "@mui/material";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HomeIcon from '@mui/icons-material/Home';
import { useTranslation } from "react-i18next";
import { Dayjs } from "dayjs";
import DatePickerComponent from "./DatePickerComponent";
import DescriptionField from "./DescriptionField";
import { RefObject } from "react";
import IconCircleWrapper from "../../../ui/IconCircleWrapper";
import { CaretRightIcon } from "../../../ui/icons";
import DynamicSvgIcon from "../../../ui/icons/DynamicSvgIcon";

interface Category {
  category: string;
  id: string;
  name: string;
  icon?: string;
  iconColor?: string;
  backgroundColor?: string;
}

interface Wallet {
  title: string;
  id: string;
  wallet: {
    id: string;
    name: string;
    balance: number;
    type: string;
  };
}

interface IncomeTransactionFormProps {
  incomeCategory: Category | null;
  incomeWallet: Wallet | null;
  datePicker: Dayjs | undefined;
  description: string;
  openDatePicker: boolean;
  setOpenDialogSelectedIncomeCategory: (open: boolean) => void;
  setOpenDialogSelectWallets: (open: boolean) => void;
  setOpenDatePicker: (open: boolean) => void;
  setDescription: (description: string) => void;
  setDatePicker: (date: Dayjs | undefined) => void;
  descriptionInputRef?: RefObject<HTMLInputElement>;
}

const IncomeTransactionForm = ({
  incomeCategory,
  incomeWallet,
  datePicker,
  description,
  openDatePicker,
  setOpenDialogSelectedIncomeCategory,
  setOpenDialogSelectWallets,
  setOpenDatePicker,
  setDescription,
  setDatePicker,
  descriptionInputRef
}: IncomeTransactionFormProps) => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'transactions']);

  return (
    <Stack spacing={4} width="100%">
      {/* Category */}
      <Stack
        direction="row"
        spacing={theme.spacing(2)}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
          {incomeCategory?.icon ? (
            <Card elevation={0} sx={{ borderRadius: '50%', overflow: 'hidden', display: 'inline-block' }}>
              <DynamicSvgIcon
                svgUrl={incomeCategory.icon}
                color={incomeCategory.iconColor}
                backgroundColor={incomeCategory.backgroundColor}
                size={30}
                iconSize={18}
              />
            </Card>
          ) : (
            <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
              <HomeIcon sx={{ fontSize: 18 }} />
            </IconCircleWrapper>
          )}
        </Stack>
        {incomeCategory ? (
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={() => setOpenDialogSelectedIncomeCategory(true)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {t('income_category', { ns: 'transactions' })}
            </Typography>
            <Typography variant="h3">{incomeCategory.name}</Typography>
          </Stack>
        ) : (
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={() => setOpenDialogSelectedIncomeCategory(true)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {t('income_category', { ns: 'transactions' })}
            </Typography>
            <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
          </Stack>
        )}
      </Stack>

      {/* Wallet */}
      <Stack
        direction="row"
        spacing={theme.spacing(2)}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={theme.spacing(2)} alignItems="center">
          <IconCircleWrapper size={30} bgcolor={theme.palette.navy[800]} color={theme.palette.common.white}>
            <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
          </IconCircleWrapper>
        </Stack>
        {incomeWallet ? (
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
          >
            <Typography variant="body1" color="secondary">
              {t('income_wallet', { ns: 'transactions' })}
            </Typography>
            <Typography
              variant="h3"
              onClick={() => setOpenDialogSelectWallets(true)}
              sx={{ cursor: 'pointer' }}
            >
              {incomeWallet.wallet.name}
            </Typography>
          </Stack>
        ) : (
          <Stack
            borderBottom={`1px solid ${theme.palette.divider}`}
            width="100%"
            alignItems="end"
            direction="row"
            justifyContent="space-between"
            paddingBottom={theme.spacing(2)}
            onClick={() => setOpenDialogSelectWallets(true)}
            sx={{ cursor: 'pointer' }}
          >
            <Typography variant="body1" color="secondary">
              {t('income_wallet', { ns: 'transactions' })}
            </Typography>
            <CaretRightIcon style={{ fontSize: 24, color: theme.palette.text.primary }} />
          </Stack>
        )}
      </Stack>

      {/* Date */}
      <DatePickerComponent
        datePicker={datePicker}
        setDatePicker={setDatePicker}
        openDatePicker={openDatePicker}
        setOpenDatePicker={setOpenDatePicker}
        clearable={false}
      />

      <DescriptionField 
        description={description} 
        setDescription={setDescription} 
        ref={descriptionInputRef}
      />
    </Stack>
  );
};

export default IncomeTransactionForm; 